import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Field, Form, Formik, FormikProps } from 'formik';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { getAppraisal } from '../../../../../../api/actions/appraisal/appraisal-get';
import { appraisalSave } from '../../../../../../api/actions/appraisal/appraisal-save';
import { Button } from '../../../../../../components/shared/button/button';
import { Loader } from '../../../../../../components/shared/loader/loader';
import { basePerformError } from '../../../../../../helpers/error-helpers';
import { IAppraisal } from '../../../../../../models/appraisal';
import { IGoal } from '../../../../../../models/goal';
import { IParticipant } from '../../../../../../models/participant';
import { IDictionary } from '../../../../../../types/dictionary';
import styles from './appraisal-editor.module.scss';
import { SelectField } from '../../../../../../components/shared/select/select';
import moment from 'moment';

interface IProps extends RouteComponentProps {
    uid?: string,
    participantUid: string,
    goals: IGoal[],
    falseAppraisal?: boolean,
    onClose: () => void,
    reload(): void,
}

enum FormFields {
    description = 'description',
    goalCongruent = 'goalCongruent',
}

interface IForm {
    [FormFields.description]?: string,
    [FormFields.goalCongruent]?: string,
}

function AppraisalEditor(props: IProps) {

    const [loading, setLoading] = useState(false);
    const [appraisal, setAppraisal] = useState<IAppraisal>();

    const loadData = useCallback(async (uid?: string) => {
        try {
            if(uid || props.uid) {
                setAppraisal(await getAppraisal(uid || props.uid || ''));
            }
            else {
                setAppraisal({} as IAppraisal);
            }
        }
        catch(err) {
            basePerformError(err, props.history);
        }
    }, [props.history, props.uid]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleSubmit = async (
        values: IForm,
        { setSubmitting, setErrors }: { setSubmitting: (status: boolean) => void, setErrors: (errors: IDictionary<string>) => void },
    ) => {
        setLoading(true);
        try {
            const newAppraisal: Partial<IAppraisal> = {
                uid: appraisal?.uid,
                description: values.description,
                goalCongruent: values.goalCongruent === 'yes' ? true : false,
            };
            if(!newAppraisal.uid) {
                if(props.falseAppraisal) {
                    newAppraisal.nonPerformer = { uid: props.participantUid } as IParticipant;
                }
                else {
                    newAppraisal.performer = { uid: props.participantUid } as IParticipant;
                }
            }
            const uid = await appraisalSave(newAppraisal);
            await loadData(uid);
            props.reload();
            toast.success('Item has been successfully saved');

        }
        catch (err) {
            basePerformError(err, props.history);
        }
        setSubmitting(false);
        setLoading(false);
    };

    const getValidationSchema = () => {
        return Yup.object<IForm>({
            description: Yup.string().trim().required().min(3).max(255),
            goalCongruent: Yup.string().label('Goal Congruent').required(),
        });
    };

    const renderForm = ({ errors, touched, isValid }: FormikProps<IForm>): React.ReactElement => {
        return (
            <div className={styles.formContainer}>
                <Form noValidate>
                    <div className="form-item">
                        <label>
                            <div className="form-label required">
                                Description
                            </div>
                            <Field component="textarea" name={FormFields.description} />
                        </label>
                        <div className="errors">{touched.description && errors.description}</div>
                    </div>
                    <div className="form-item">
                        <label>
                            <div className="form-label required">
                                Goal Congruent
                            </div>
                            <Field
                                component={SelectField}
                                name={FormFields.goalCongruent}
                                emptyTitle=""
                                data={[
                                    { uid: 'yes', name: 'Yes' },
                                    { uid: 'no', name: 'No' },
                                ]}
                            />
                        </label>
                        <div className="errors">{touched.goalCongruent && errors.goalCongruent}</div>
                    </div>
                    { appraisal?.createdBy && (
                        <div className="form-item">
                            <label>
                                <div className="form-label">
                                    Created
                                </div>
                                <div>
                                    <strong>
                                        {appraisal.createdBy?.firstName}
                                        &nbsp;
                                        {appraisal.createdBy?.lastName}
                                    </strong>
                                    &nbsp; at &nbsp;
                                    {moment(appraisal.createdAt).format('ddd, MM/DD/YYYY h:mm a')}
                                </div>
                            </label>
                        </div>
                    )}
                    { appraisal?.updatedBy && (
                        <div className="form-item">
                            <label>
                                <div className="form-label">
                                    Updated
                                </div>
                                <div>
                                    <strong>
                                        {appraisal.updatedBy?.firstName}
                                        &nbsp;
                                        {appraisal.updatedBy?.lastName}
                                    </strong>
                                    &nbsp; at &nbsp;
                                    {moment(appraisal.updatedAt).format('ddd, MM/DD/YYYY h:mm a')}
                                </div>
                            </label>
                        </div>
                    )}

                    <div className="form-buttons">
                        <Button onClick={props.onClose} className="gray small">
                            <FontAwesomeIcon icon={faArrowLeft} /> <span>Back</span>
                        </Button>
                        { loading
                            ? (<Loader />)
                            : (<Button type="submit" className="small"><span>{appraisal?.uid ? 'Save' : 'Create'}</span></Button>)
                        }
                    </div>
                </Form>
            </div>
        );
    };

    return !appraisal ? (<Loader />) : (
        <Formik
            initialValues={{
                description: (appraisal && appraisal.description) || '',
                goalCongruent: (
                    appraisal && (appraisal.goalCongruent !== undefined && (appraisal.goalCongruent ? 'yes' : 'no'))
                ) || undefined,
            }}
            validationSchema={getValidationSchema}
            onSubmit={handleSubmit}
        >
            {renderForm}
        </Formik>
    );
}

const AppraisalEditorWithRouter = withRouter(AppraisalEditor);
export { AppraisalEditorWithRouter as AppraisalEditor };

